// src/pages/LanguageCMS/LanguageCMS.jsx
import { useEffect, useState } from "react";
import styles from "./LanguageCMS.module.scss";
import ImageUploader from "@/components/ImageUploader/ImageUploader";
import api from "@/utils/axios"; // baseURL: "/api"

const LANGS = ["en", "si", "ta", "hi"];

export default function LanguageCMS() {
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState({ en: "", si: "", ta: "", hi: "" });
  const [subtitle, setSubtitle] = useState({ en: "", si: "", ta: "", hi: "" });
  const [message, setMessage] = useState("");

  // Load current published LanguageCMS
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/v1/languageCMS");
        const lang = data?.language || data || {};
        if (!alive) return;

        setDoc(lang);
        if (lang.title) setTitle((t) => ({ ...t, ...lang.title }));
        if (lang.subtitle) setSubtitle((s) => ({ ...s, ...lang.subtitle }));
      } catch (e) {
        console.warn("[LanguageCMS] load failed:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  function onTextChange(group, key) {
    return (e) => {
      const v = e.target.value;
      if (group === "title") setTitle((t) => ({ ...t, [key]: v }));
      if (group === "subtitle") setSubtitle((s) => ({ ...s, [key]: v }));
    };
  }

  async function saveTexts() {
    setMessage("");
    try {
      const payload = { title, subtitle };

      await api.post("/v1/languageCMS/save-texts", payload);
      setDoc((prev) => ({
        ...(prev || {}),
        title: { ...(prev?.title || {}), ...title },
        subtitle: { ...(prev?.subtitle || {}), ...subtitle },
      }));

      setMessage("✅ Language texts saved & published.");
    } catch (e) {
      console.error(e);
      setMessage(
        `❌ Failed to save text${
          e?.response?.status ? ` (${e.response.status})` : ""
        }`
      );
    }
  }

  // Where the uploader should fetch the Bearer token (adjust if different)
  const token =
    typeof window !== "undefined" ? localStorage.getItem("cm_at") || "" : "";

  return (
    <div className={styles.wrap}>
      <h1>Language CMS</h1>
      <p className={styles.help}>
        Edit titles & subtitles in 4 languages. Upload images
        per-slot—processing and publishing are automatic once ready.
      </p>

      {/* Titles */}
      <div className={styles.grid2}>
        <div className={styles.card}>
          <h3>Title</h3>
          {LANGS.map((k) => (
            <label key={k} className={styles.label}>
              {k.toUpperCase()}
              <input
                value={title[k] ?? ""}
                onChange={onTextChange("title", k)}
                className={styles.input}
                placeholder={`Title (${k})`}
              />
            </label>
          ))}
        </div>

        <div className={styles.card}>
          <h3>Subtitle</h3>
          {LANGS.map((k) => (
            <label key={k} className={styles.label}>
              {k.toUpperCase()}
              <input
                value={subtitle[k] ?? ""}
                onChange={onTextChange("subtitle", k)}
                className={styles.input}
                placeholder={`Subtitle (${k})`}
              />
            </label>
          ))}
          <div className={styles.row}>
            <button
              type="button"
              className={`nu-btn ${styles.btnPrimary}`}
              onClick={saveTexts}
              title="Save titles/subtitles"
            >
              Save text
            </button>
          </div>
        </div>
      </div>

      {/* Images */}
      <div className={styles.card}>
        <h3>Hero Images (4) — auto-publish</h3>

        <div className={styles.grid2}>
          <ImageUploader
            label="Mr Desktop"
            slot="desktopPrimary"
            initial={doc?.images?.desktopPrimary || null}
            token={token}
            createJobPath="/v1/languageCMS/images/create-job"
            autoRefresh={true} // optional
            refreshDocPath="/v1/languageCMS"
            selectSlot={(res, slot) => (res?.language || res)?.images?.[slot]}
            onReady={(img) => {
              setDoc((prev) => ({
                ...(prev || {}),
                images: { ...(prev?.images || {}), desktopPrimary: img },
              }));
              setMessage(
                "✅ Mr Desktop (Language) image processed & published."
              );
            }}
          />

          <ImageUploader
            label="Mr Mobile"
            slot="mobilePrimary"
            initial={doc?.images?.mobilePrimary || null}
            token={token}
            createJobPath="/v1/languageCMS/images/create-job"
            autoRefresh={true} // optional
            refreshDocPath="/v1/languageCMS"
            selectSlot={(res, slot) => (res?.language || res)?.images?.[slot]}
            onReady={(img) => {
              setDoc((prev) => ({
                ...(prev || {}),
                images: { ...(prev?.images || {}), mobilePrimary: img },
              }));
              setMessage(
                "✅ Mr Mobile (Language) image processed & published."
              );
            }}
          />

          <ImageUploader
            label="Mrs Desktop"
            slot="desktopSecondary"
            initial={doc?.images?.desktopSecondary || null}
            token={token}
            createJobPath="/v1/languageCMS/images/create-job"
            autoRefresh={true} // optional
            refreshDocPath="/v1/languageCMS"
            selectSlot={(res, slot) => (res?.language || res)?.images?.[slot]}
            onReady={(img) => {
              setDoc((prev) => ({
                ...(prev || {}),
                images: { ...(prev?.images || {}), desktopSecondary: img },
              }));
              setMessage(
                "✅ Mrs Desktop (Language) image processed & published."
              );
            }}
          />

          <ImageUploader
            label="Mrs Mobile"
            slot="mobileSecondary"
            initial={doc?.images?.mobileSecondary || null}
            token={token}
            createJobPath="/v1/languageCMS/images/create-job"
            autoRefresh={true} // optional
            refreshDocPath="/v1/languageCMS"
            selectSlot={(res, slot) => (res?.language || res)?.images?.[slot]}
            onReady={(img) => {
              setDoc((prev) => ({
                ...(prev || {}),
                images: { ...(prev?.images || {}), mobileSecondary: img },
              }));
              setMessage(
                "✅ Mrs Mobile (Language) image processed & published."
              );
            }}
          />
        </div>
      </div>

      {message && <p className={styles.message}>{message}</p>}
    </div>
  );
}
